import React from 'react'
import { Maybe } from 'graphql/jsutils/Maybe'
import { Case, Default, Switch, When } from 'react-if'
import { InView } from 'react-intersection-observer'
import { LinkButton } from '@/atoms/Button'
import { ResponsiveFlexRow } from '@/atoms/FlexContainers'
import { AsH2, AsLabel, CaptionMD, CaptionXS, HeadingMD, LabelLG, TitleLG } from '@/atoms/Text'
import { PRICE_ANCHOR_EXPERIMENT_VARIANTS } from '@/constants/guild'
import { ThemeContextProvider } from '@/contexts/ThemeContext'
import { FaqAccordion } from '@/organisms/FaqAccordion'
import { Page } from '@/page'
import {
  isGuildExplainerLayout,
  isGuildSignupSimpleLayout,
  isGuildSignupVideoLayout,
} from '@/services/GuildSignupPageService/validations'
import { PageDataContext, PageModel } from '@/services/RenderService'
import { Faq } from '@/types/codegen-contentful'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GuildApprovals } from '@/views/GuildJoinView/GuildApprovals'
import { GuildHowDoesItWorkV2 } from '@/views/GuildJoinView/GuildHowDoesItWorkV2'
import { GuildNowStreamingRail } from '@/views/GuildSignupView/GuildBodySection/GuildNowStreamingRail'
import { GuildUpcomingRail } from '@/views/GuildSignupView/GuildBodySection/GuildUpcomingRail'
import { GuildExplainerLayout } from '@/views/GuildSignupView/GuildExplainerLayout'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'
import { AppPromoSection } from '@/views/LandingView/AppPromoSection'
import { DryBarPlusOffer } from '@/views/MarketingWinbackView/DryBarPlusOffer'
import { MarketingWinbackHero } from '@/views/MarketingWinbackView/MarketingWinbackHero'
import { OmniGuildPlanSelectionCard } from '../GuildJoinView/OmniGuildPlanSelectionCard'
import { MarketingWinbackVideoHero } from './MarketingWingbackVideoHero'

interface MarketingWinbackViewProps {
  pageDataContext: Maybe<PageDataContext>
  page: Maybe<PageModel>
}

export const MarketingWinbackView: React.FC<MarketingWinbackViewProps> = ({ page, pageDataContext }) => {
  const hasUser = true
  const { layout, guildExplainerLayout, guildFaqs, slug } = useGuildSignupContext()
  const { t } = useTranslate('guild')
  const { trackProductViewed } = useEcommerceEvents()

  const isVideoHeroLayout = isGuildSignupVideoLayout(layout)

  const isSimpleLayout = isGuildSignupSimpleLayout(layout)
  const canShowExplainerLayout = isGuildExplainerLayout(guildExplainerLayout)

  const isDBCPlusOffer = slug === 'dbcplus'
  const isTmtDec25Offer = slug === 'tmtdec25'

  return (
    <main className="h-full w-full se:overflow-x-hidden">
      <ThemeContextProvider isDarkMode={!isSimpleLayout}>
        <Switch>
          <Case condition={isVideoHeroLayout}>
            <MarketingWinbackVideoHero />
          </Case>
          <Default>
            <MarketingWinbackHero />
          </Default>
        </Switch>
        <div id={'plan-selection'} className="bg-[#222222]">
          <Switch>
            <Case condition={isDBCPlusOffer}>
              <DryBarPlusOffer />
            </Case>
            <Default>
              <TitleLG weight="semibold" color="white" className="py-8 text-center lg:pt-16">
                <Translate i18nKey="supportFilmmakersCancelAnytime" t={t}>
                  Support films & filmmakers that share your values. Cancel anytime.
                </Translate>
              </TitleLG>
            </Default>
          </Switch>
          <ResponsiveFlexRow className="mx-auto flex w-full max-w-[1235px] gap-4 md:!flex-col md:!items-stretch md:gap-8">
            <InView
              triggerOnce
              onChange={(inView) => {
                if (!inView) return

                trackProductViewed({
                  category: 'guild',
                  funnel: 'guild',
                  name: 'Angel Guild Plans',
                  price: 0,
                  currency: 'USD',
                  quantity: 1,
                  product_id: 'Angel Guild Plans',
                  sku: 'Angel Guild Plans',
                  variant: 'basic monthly, basic yearly, premium monthly, premium yearly',
                })
              }}
              className="flex flex-col items-center"
            >
              <ResponsiveFlexRow className="mx-auto flex w-full max-w-[1235px] items-center justify-center gap-4 pb-8 md:!items-stretch md:gap-8">
                <OmniGuildPlanSelectionCard
                  className="mt-0"
                  darkMode={false}
                  hideLabels
                  isSignupLayout={false}
                  guildOfferPackage={PRICE_ANCHOR_EXPERIMENT_VARIANTS['18-169 / 20-179']}
                  defaultSelectedOffer={isTmtDec25Offer ? 'premium' : 'basic'}
                  promotedInterval={isTmtDec25Offer ? 'monthly' : 'annual'}
                />
              </ResponsiveFlexRow>
              <div className="w-full items-center justify-center px-4 py-2 text-center md:py-6">
                <CaptionXS weight="medium" color="white" className="md:photon-caption-sm">
                  <Translate t={t} i18nKey="fullHdUltraUdAvailabilityDisclaimer">
                    Full HD (1080p) and Ultra HD (4K) availability are subject to your internet service and device
                    capabilities. Not all content is available in all resolutions.
                  </Translate>
                </CaptionXS>
              </div>
            </InView>
          </ResponsiveFlexRow>
        </div>

        {!!page && !!pageDataContext && (
          <section className="relative bg-core-gray-950 py-16">
            <div className="absolute top-0 h-1/2 w-full bg-gradient-to-b from-[#222222] to-transparent" />
            <div className="relative flex flex-col gap-y-12">
              <Page page={page} pageDataContext={{ ...pageDataContext, contentItemConfig: { padding: true } }} />
            </div>
          </section>
        )}

        <div className="overflow-hidden">
          <GuildApprovals />
        </div>
        <AppPromoSection containerClassName="h-full pt-10 !pb-0" simplified />
        <When condition={canShowExplainerLayout}>
          {guildExplainerLayout && (
            <GuildExplainerLayout guildExplainerLayout={guildExplainerLayout} hasUser={hasUser} hideButton />
          )}
        </When>
        <GuildHowDoesItWorkV2 />
        <section className="mb-12 flex items-center justify-center">
          <div className="flex w-full max-w-[600px] flex-col items-center justify-center !opacity-100">
            <HeadingMD as={AsH2} weight="bold" color="white" className="mb-6 text-center">
              <Translate t={t} i18nKey="readyToHelp">
                Ready to help?
              </Translate>
            </HeadingMD>
            <div className="mb-6 py-3">
              <CaptionMD weight="semibold" color="white" className="text-center">
                <Translate i18nKey="lockInYourSavings" t={t}>
                  Lock in your savings and join the mission to bring the world stories that amplify light.
                </Translate>
              </CaptionMD>
            </div>
            <LinkButton
              href="#plan-selection"
              variant="oxide"
              className={'btn my-0 mb-4 min-h-[40px] min-w-[142px] rounded-lg py-2 lg:min-h-[56px] lg:w-2/5 lg:px-0'}
            >
              <LabelLG
                as={AsLabel}
                color="white"
                weight="semibold"
                className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
              >
                <Translate i18nKey="getStarted" t={t}>
                  Get Started
                </Translate>
              </LabelLG>
            </LinkButton>
          </div>
        </section>

        <section className="mb-12 overflow-x-hidden px-4 scrollbar-none sm:px-8 md:px-12 xl:px-16">
          <GuildNowStreamingRail />
        </section>
        <section className="mb-12 overflow-x-hidden px-4 scrollbar-none sm:px-8 md:px-12 xl:px-16">
          <GuildUpcomingRail />
        </section>
        <section
          aria-label={t('frequentlyAskedQuestions', 'Frequently Asked Questions')}
          id="faqs"
          key="faqs"
          className="mx-auto scroll-mt-[73px] px-8 py-16 text-left text-white md:max-w-[654px]"
        >
          <FaqAccordion faqs={guildFaqs as Faq[]} showTopBorder={true} />
        </section>
      </ThemeContextProvider>
    </main>
  )
}
